import { cva, VariantProps } from 'class-variance-authority';
import { create, useStore } from 'zustand';
import { devtools } from 'zustand/middleware';

/** Generate a random unique-enough string */
const generateId = () => Math.random().toString(36).substring(2);

export const toastVariants = cva(['fixed flex flex-col gap-3'], {
  variants: {
    position: {
      topRight: ['inset-x-2 top-6 sm:left-auto sm:w-[360px]'],
      bottomCenter: ['inset-x-4 bottom-2 left-1/2 w-[360px] -translate-x-1/2'],
    },
  },
  defaultVariants: {
    position: 'topRight',
  },
});

/** Necessary data to create a toast */
type ToastParams = {
  /** A toast status */
  status: 'success' | 'error' | 'warning' | 'info';

  /** An optional title */
  title?: string;

  /** Content of a toast */
  description?: string;

  /** A toast duration */
  duration?: number;

  /** Text for an optional CTA */
  ctaText?: string;

  /** Event for an optional CTA */
  onCTAClick?: () => void;

  /** Whether the CTA should be disabled (defaults to false if not specified) */
  disabledCTA?: boolean;

  /** Whether to show the status icon (defaults to true if not specified) */
  showIcon?: boolean;

  /** Where a toast should be positioned (defaults to topRight if not specified) */
} & VariantProps<typeof toastVariants>;

/** Internal toast object structure */
type ToastData = ToastParams & {
  /** A unique identifier of a toast that can be used to dismiss it */
  id: string;

  /** Toast creation timestamp */
  createdAt: number;
};

type ToastManagerStore = {
  /** A map of ids to toast objects */
  toastsById: Record<string, ToastData>;

  /** An array of toast ids that preserves the insertion order */
  toastIds: string[];

  /**
   * Pushes a new toast into the list
   *
   * @param toastData - data necessary to open a toast
   * @returns the id of a created toast, which can be used to dismiss the toast
   */
  showToast(toastData: ToastParams): string;

  /**
   * Dismisses a toast from the list
   *
   * @param toastId - the id of a toast to be dismissed
   */
  dismissToast(toastId: string): void;
};

export const toastManagerStore = create<ToastManagerStore>()(
  devtools(
    (set) => ({
      toastsById: {},
      toastIds: [] as string[],

      showToast(toastData) {
        const id = generateId();

        set(
          (prevState) => {
            const toast = { ...toastData, id, createdAt: Date.now() };

            return {
              toastsById: { ...prevState.toastsById, [id]: toast },
              toastIds: [...prevState.toastIds, id],
            };
          },
          false,
          'toastManager/showToast'
        );

        return id;
      },

      dismissToast(toastId) {
        set(
          (prevState) => {
            const nextToastsById = { ...prevState.toastsById };
            delete nextToastsById[toastId];

            const nextToastIds = prevState.toastIds.filter(
              (id) => id !== toastId
            );

            return {
              toastsById: nextToastsById,
              toastIds: nextToastIds,
            };
          },
          false,
          'toastManager/dismissToast'
        );
      },
    }),
    { name: 'toastManager' }
  )
);

export const showToast = (toastData: ToastParams): string =>
  toastManagerStore.getState().showToast(toastData);

export const dismissToast = (toastId: string): void => {
  toastManagerStore.getState().dismissToast(toastId);
};

export const useIsToastOpen = (toastId: string | null): boolean => {
  const { toastIds } = useStore(toastManagerStore);

  return toastId ? toastIds.includes(toastId) : false;
};
